import React from "react";
import BarekamText from "../../../components/text";
import BarekamChip from "../../../components/chip/barekamChip";
import { BarekamIcon, IconVariants } from "../../../components/icon/barekam-icon";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import { AddressMenuItem } from "./styled";
import { WarehouseModel } from "../../../shared/models/order/warehouse.model";
import { useAppSelector } from "../../../../redux/store";
import { User } from "../../../shared/models/user/user";
import BarekamButton, { HeaderButtons } from "../../../components/button";
import { getMessage, initToast } from "../../../shared/helper-functions";
import { ToastContainer } from "react-toastify";

const WarehouseAddressInfo = ({ warehouse }: AddressMenuItemProps) => {
    const { currentUser } = useAppSelector(state => state.currentUserReducer);
    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    async function copy(data: WarehouseModel | User | undefined, field: string) {
        const fieldValue = await getFieldValue(data, field);
        if (field !== 'addressLine2ForAir') {
            initToast(
                `${fieldValue} ${getMessage(
                    "copied",
                    MessageSections.ORDERS,
                    messageCache,
                    languageId,
                )
                }`,
                "success",
                200
            );
            return navigator.clipboard.writeText(fieldValue || '');
        }
    }

    async function getFieldValue(data: WarehouseModel | User | undefined, field: string) {
        switch (field) {
            case 'address1':
                return (data as WarehouseModel).addressLine1;
            case 'zipCode':
                return (data as WarehouseModel).zipCode;
            case 'phone':
                return (data as WarehouseModel).phone;
            case 'city':
                return (data as WarehouseModel).city;
            case 'state':
                return (data as WarehouseModel).state;
            case 'addressLine2ForAir':
                return (data as User).addressLine2ForAir;
            case 'addressLine2ForLand':
                return (data as User).addressLine2ForLand;
        }
    }

    async function copyAll() {
        if (currentUser) {
            initToast(
                getMessage(
                    "all_copied",
                    MessageSections.ORDERS,
                    messageCache,
                    languageId,
                ),
                "success",
            );
            const text = `${warehouse.city}\n${warehouse.state}\n${warehouse.addressLine1}\n${currentUser.addressLine2ForLand}\n${warehouse.zipCode}\n${warehouse.phone}`
            return navigator.clipboard.writeText(text);
        }
    }

    return (
        <div style={{ backgroundColor: '#F1F1FB', borderRadius: '0.5rem', marginRight: '0.6rem' }}>
            <AddressMenuItem key={warehouse.city} onClick={() => copy(warehouse, 'city')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"city"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    <BarekamText variant={"s"} fontWeight={500}>
                        {warehouse.city}
                    </BarekamText>
                </div>

                <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)} />
            </AddressMenuItem>

            <AddressMenuItem key={warehouse.state} onClick={() => copy(warehouse, 'state')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"state"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    <BarekamText variant={"s"} fontWeight={500}>
                        {warehouse.state}
                    </BarekamText>
                </div>

                <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)} />
            </AddressMenuItem>

            <AddressMenuItem key={warehouse.addressLine1} onClick={() => copy(warehouse, 'address1')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"address_line_1"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    <BarekamText variant={"s"} fontWeight={500}>
                        {warehouse.addressLine1}
                    </BarekamText>
                </div>

                <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)} />
            </AddressMenuItem>

            <AddressMenuItem key={'address2_for_air'}
                onClick={() => copy(currentUser, 'addressLine2ForAir')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"address_line_2_for_air"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    {/* <BarekamText variant={"s"} fontWeight={500}>
                        {currentUser?.addressLine2ForAir}
                    </BarekamText> */}
                    <BarekamChip messageKey={'coming_soon'} statusId={4} chipSize={'s'} />
                </div>

                {/* <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)}/> */}
            </AddressMenuItem>

            <AddressMenuItem key={'address2_for_land'}
                onClick={() => copy(currentUser, 'addressLine2ForLand')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"address_line_2_for_land"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    <BarekamText variant={"s"} fontWeight={500}>
                        {currentUser?.addressLine2ForLand}
                    </BarekamText>
                </div>

                <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)} />
            </AddressMenuItem>

            <AddressMenuItem key={warehouse.zipCode} onClick={() => copy(warehouse, 'zipCode')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"postal_code"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    <BarekamText variant={"s"} fontWeight={500}>
                        {warehouse?.zipCode?.toString()}
                    </BarekamText>
                </div>

                <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)} />
            </AddressMenuItem>

            <AddressMenuItem key={warehouse.phone} onClick={() => copy(warehouse, 'phone')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '0.7rem' }}>
                    <BarekamText variant={"s"} fontWeight={500} opaque>
                        <Message messageKey={"address_line_phone"} sectionId={MessageSections.MAIN} />
                        :
                    </BarekamText>

                    <BarekamText variant={"s"} fontWeight={500}>
                        {warehouse.phone}
                    </BarekamText>
                </div>

                <BarekamIcon variant={IconVariants.copy} onClick={() => void (0)} />
            </AddressMenuItem>

            <AddressMenuItem>
                <BarekamButton type={HeaderButtons.HEADER_MY_ADDRESSES_COPY} onClick={() => copyAll()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.7rem' }}>
                        <BarekamIcon variant={IconVariants.copy} />
                        <BarekamText variant={"m"} fontWeight={700} bold>
                            <Message messageKey={"copy_all"} sectionId={MessageSections.MAIN} />
                        </BarekamText>
                    </div>
                </BarekamButton>
            </AddressMenuItem>
            <ToastContainer />
        </div>
    )
};

interface AddressMenuItemProps {
    warehouse: WarehouseModel
}

export default WarehouseAddressInfo;